import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import {
  SharedBannerComponent,
  SharedBannerComponentSchema,
} from "@intelligems/ig-types";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import { getDefaultBanner } from "@src/store/slices/experienceBuilder/component/defaults";
import {
  BannerComponent,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";

import { escapeAttr as esc } from "./escapeAttr";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";

function toHtml(mod: BannerComponent): string {
  return `<ig-banner-component
    icon-position="${esc(mod.iconPosition)}"
    icon-src="${esc(mod.iconSrc ?? "")}"
    background-color="${esc(mod.backgroundColor)}"
    stroke-color="${esc(mod.strokeColor)}"
    stroke-width="${esc(mod.strokeWidth)}"
    corner-radius="${esc(mod.cornerRadius)}"
    padding="${esc(mod.padding)}"
    margin="${esc(mod.margin)}"
    line-height="${esc(mod.lineHeight)}"
    letter-spacing="${esc(mod.letterSpacing)}"
  >${mod.content}</ig-banner-component>`;
}

function fromHtml(html: string): Partial<BannerComponent> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const el = doc.querySelector("ig-banner-component");
  if (!el) return {};
  return {
    content: el.innerHTML,
    iconSrc: el.getAttribute("icon-src") ?? undefined,
    iconPosition:
      (el.getAttribute("icon-position") as BannerComponent["iconPosition"]) ??
      "left",
    backgroundColor:
      el.getAttribute("background-color") ?? IgGrayPrimitives[400],
    strokeColor: el.getAttribute("stroke-color") ?? IgGrayPrimitives[800],
    strokeWidth: el.getAttribute("stroke-width") ?? "1",
    cornerRadius: el.getAttribute("corner-radius") ?? "8",
    padding: el.getAttribute("padding") ?? "16",
    margin: el.getAttribute("margin") ?? "0",
    lineHeight: el.getAttribute("line-height") ?? "1.5",
    letterSpacing: el.getAttribute("letter-spacing") ?? "0",
  };
}

export const bannerComponentHandler: ModHandler<BannerComponent> = {
  normalize: (mod) => mod,

  convertFrom: (mod) => ({
    type: "onsiteEdit",
    dto: {
      id: mod.id,
      variationId: mod.variationId,
      querySelectors: [mod.selector],
      onsiteEditType: "html",
      replace: toHtml(mod),
      find: "",
      isRegex: false,
      skip: false,
      remove: false,
      isHtml: false,
      isImage: false,
      insertType: mod.insertType === "replace" ? null : mod.insertType,
      createdAtUrl: mod.createdAtUrl,
      source: mod.source ?? null,
      title: mod.description ?? null,
    },
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteEdit")
      throw new Error("Expected onsiteEdit");
    const edit = conversion.dto;
    const parsed = fromHtml(edit.replace);
    return {
      type: "bannerComponent",
      category: "component",
      id: edit.id,
      variationId: edit.variationId,
      selector: edit.querySelectors.join(","),
      insertType: edit.insertType ?? "replace",
      createdAtUrl: edit.createdAtUrl ?? undefined,
      source: edit.source ?? undefined,
      content: parsed.content ?? "",
      iconSrc: parsed.iconSrc,
      iconPosition: parsed.iconPosition ?? "left",
      backgroundColor: parsed.backgroundColor ?? IgGrayPrimitives[400],
      strokeColor: parsed.strokeColor ?? IgGrayPrimitives[800],
      strokeWidth: parsed.strokeWidth ?? "1",
      cornerRadius: parsed.cornerRadius ?? "8",
      padding: parsed.padding ?? "16",
      margin: parsed.margin ?? "0",
      lineHeight: parsed.lineHeight ?? "1.5",
      letterSpacing: parsed.letterSpacing ?? "0",
      description: edit.title ?? undefined,
    };
  },

  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    if (!mod.selector) {
      errors.selector = "Element selection is required";
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    return errors;
  },

  isEquivalent: (a, b) =>
    b.type === "bannerComponent" &&
    a.selector === b.selector &&
    a.insertType === b.insertType,

  defaults: () =>
    getDefaultBanner({
      id: v4(),
      variationId: "",
      selector: "",
      insertType: "beforebegin",
    }),

  toAiFormat: (mod) =>
    pickSharedFields<SharedBannerComponent>(SharedBannerComponentSchema, mod),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? bannerComponentHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod, newMod) => {
    const pick = (mod: BannerComponent) => ({
      content: mod.content,
      iconSrc: mod.iconSrc,
      iconPosition: mod.iconPosition,
      backgroundColor: mod.backgroundColor,
      strokeColor: mod.strokeColor,
      strokeWidth: mod.strokeWidth,
      cornerRadius: mod.cornerRadius,
      padding: mod.padding,
      margin: mod.margin,
      lineHeight: mod.lineHeight,
      letterSpacing: mod.letterSpacing,
    });
    return !isEqual(pick(oldMod), pick(newMod));
  },
};
